import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import {
  Building2, Mail, Phone, Globe, FileText, Save, RefreshCw,
  Shield, MapPin, Hash, ExternalLink, Info
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const FIELDS = [
  { section: 'Información de la Empresa', icon: Building2, fields: [
    { key: 'name', label: 'Nombre Comercial', placeholder: 'OkReserva', required: true },
    { key: 'legal_name', label: 'Razón Social', placeholder: 'OkGo S.A.S. de C.V.', required: true },
    { key: 'rfc', label: 'RFC', placeholder: 'OGO240101ABC', required: false },
  ]},
  { section: 'Domicilio Fiscal', icon: MapPin, fields: [
    { key: 'address', label: 'Dirección (calle y número)', placeholder: 'Av. Reforma 123, Col. Juárez', required: false },
    { key: 'city', label: 'Ciudad', placeholder: 'Ciudad de México', required: false },
    { key: 'state', label: 'Estado', placeholder: 'Ciudad de México', required: false },
    { key: 'zip_code', label: 'Código Postal', placeholder: '06600', required: false },
    { key: 'country', label: 'País', placeholder: 'México', required: false },
  ]},
  { section: 'Contacto General', icon: Mail, fields: [
    { key: 'email', label: 'Correo Electrónico General', placeholder: 'hola@okgo.mx', required: true, type: 'email' },
    { key: 'support_email', label: 'Correo de Soporte', placeholder: 'soporte@okgo.mx', required: false, type: 'email' },
    { key: 'dpa_email', label: 'Correo de Privacidad (DPA)', placeholder: 'privacidad@okgo.mx', required: false, type: 'email' },
    { key: 'phone', label: 'Teléfono', placeholder: '+52 55 1234 5678', required: false, type: 'tel' },
    { key: 'website', label: 'Sitio Web', placeholder: 'https://okreserva.com', required: false, type: 'url' },
  ]},
];

export default function AdminLegal() {
  const [form, setForm] = useState({
    name: '', legal_name: '', rfc: '', address: '', city: 'Ciudad de México',
    state: 'Ciudad de México', zip_code: '', country: 'México',
    email: '', support_email: '', dpa_email: '', phone: '', website: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastUpdated, setLastUpdated] = useState('');

  const getHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/legal/company-info`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setForm(data);
        setLastUpdated(data.last_updated || '');
      }
    } catch { toast.error('Error al cargar información'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!form.name || !form.legal_name || !form.email) {
      toast.error('Completa los campos obligatorios (*)');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/legal/admin/company-info`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(form),
      });
      if (res.ok) {
        const data = await res.json();
        setLastUpdated(data.data?.last_updated || '');
        toast.success('Información legal actualizada correctamente');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Error al guardar');
      }
    } catch { toast.error('Error de conexión'); }
    finally { setSaving(false); }
  };

  const LEGAL_PAGES = [
    { label: 'Términos y Condiciones', path: '/terminos', icon: FileText },
    { label: 'Política de Privacidad', path: '/privacidad', icon: Shield },
    { label: 'Aviso Legal', path: '/aviso-legal', icon: Building2 },
    { label: 'Política de Cookies', path: '/cookies', icon: Hash },
  ];

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <RefreshCw className="w-8 h-8 text-orange-500 animate-spin" />
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-gray-900">Legal</h1>
          <p className="text-gray-500 mt-1">
            Información corporativa que aparece en las páginas legales de la plataforma
          </p>
          {lastUpdated && (
            <p className="text-xs text-gray-400 mt-1">Última actualización: {lastUpdated}</p>
          )}
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 gap-2 flex-shrink-0"
        >
          {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? 'Guardando...' : 'Guardar cambios'}
        </Button>
      </div>

      {/* Live Links */}
      <div className="bg-black rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Globe className="w-5 h-5 text-orange-400" />
          <h2 className="font-bold text-white">Páginas legales publicadas</h2>
        </div>
        <p className="text-gray-400 text-sm mb-4">
          La información que configures aquí se aplicará automáticamente en todas estas páginas públicas:
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          {LEGAL_PAGES.map(({ label, path, icon: Icon }) => (
            <a
              key={path}
              href={path}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-4 py-3 bg-white/5 hover:bg-white/10 rounded-xl transition-colors group"
            >
              <Icon className="w-4 h-4 text-orange-400 flex-shrink-0" />
              <span className="text-white text-sm font-medium flex-1">{label}</span>
              <ExternalLink className="w-3 h-3 text-gray-500 group-hover:text-orange-400 transition-colors" />
            </a>
          ))}
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-orange-50 border border-orange-200 rounded-xl px-5 py-4 flex gap-3">
        <Info className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-orange-800">
          <strong>¿Por qué es importante esto?</strong> La LFPDPPP y la LFPC requieren que las plataformas digitales publiquen información precisa sobre el responsable del tratamiento de datos. Mantén esta información actualizada para cumplir con la normativa mexicana vigente.
        </div>
      </div>

      {/* Form Sections */}
      {FIELDS.map(({ section, icon: SectionIcon, fields }) => (
        <div key={section} className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
          <div className="flex items-center gap-3 px-6 py-4 bg-gray-50 border-b border-gray-200">
            <SectionIcon className="w-5 h-5 text-orange-500" />
            <h2 className="font-bold text-gray-900">{section}</h2>
          </div>
          <div className="p-6 grid sm:grid-cols-2 gap-5">
            {fields.map(({ key, label, placeholder, required, type = 'text' }) => (
              <div key={key} className={key === 'address' ? 'sm:col-span-2' : ''}>
                <Label htmlFor={key} className="text-sm font-semibold text-gray-700 mb-1.5 block">
                  {label} {required && <span className="text-orange-500">*</span>}
                </Label>
                <Input
                  id={key}
                  type={type}
                  value={form[key] || ''}
                  onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                  placeholder={placeholder}
                  className="border-gray-200 focus:border-orange-500 focus:ring-orange-500"
                />
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Preview Panel */}
      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
        <div className="flex items-center gap-3 px-6 py-4 bg-gray-50 border-b border-gray-200">
          <FileText className="w-5 h-5 text-orange-500" />
          <h2 className="font-bold text-gray-900">Vista previa — Datos que aparecerán en páginas legales</h2>
        </div>
        <div className="p-6">
          <div className="overflow-hidden rounded-xl border border-gray-200">
            <table className="w-full text-sm">
              <tbody className="divide-y divide-gray-100">
                {[
                  ['Nombre comercial', form.name],
                  ['Razón social', form.legal_name],
                  ['RFC', form.rfc],
                  ['Domicilio', form.address ? `${form.address}, ${form.city}, ${form.state} ${form.zip_code}, ${form.country}` : '—'],
                  ['Teléfono', form.phone],
                  ['Correo general', form.email],
                  ['Correo soporte', form.support_email],
                  ['Correo privacidad', form.dpa_email],
                  ['Sitio web', form.website],
                ].map(([label, value], i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-3 font-semibold text-gray-600 w-2/5">{label}</td>
                    <td className="px-4 py-3 text-gray-700">{value || <span className="text-gray-300">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Save Button (bottom) */}
      <div className="flex justify-end pb-8">
        <Button
          onClick={handleSave}
          disabled={saving}
          size="lg"
          className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 gap-2"
        >
          {saving ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
          {saving ? 'Guardando...' : 'Guardar cambios'}
        </Button>
      </div>
    </div>
  );
}
